<template>
  <div class="console">
    <nav ref="tabsEl" class="console__tabs" aria-label="Console sections">
      <router-link
        v-for="tab in tabs"
        :key="tab.name"
        :to="{ name: tab.name }"
        class="console__tab"
        :class="{ 'console__tab--active': isTabActive(tab) }"
        :aria-label="tabAriaLabel(tab)"
      >
        {{ tab.label }}
        <span
          v-if="tab.name === 'ConsolePlans'"
          class="console__tab-live-dot"
          :class="{ 'console__tab-live-dot--busy': pendingPlanRefreshBusy }"
          aria-hidden="true"
        />
        <span
          v-if="tab.name === 'ConsolePlans' && pendingPlanCount > 0"
          class="console__tab-badge"
        >
          {{ pendingPlanCount }}
        </span>
      </router-link>
    </nav>

    <div
      class="console__pending-plan-live"
      role="status"
      aria-live="polite"
    >
      {{ pendingPlanCount }} {{ pendingPlanCount === 1 ? 'plan' : 'plans' }} pending approval
    </div>

    <div class="console__content">
      <router-view />
    </div>
  </div>
</template>

<script setup>
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useOrchestratorRequest } from '@/composables/useOrchestratorRequest.js'
import { fetchPlanCounts } from '@/services/planSummary.js'

const route = useRoute()
const { requestJson } = useOrchestratorRequest()
const tabsEl = ref(null)
const pendingPlanCount = ref(0)
const pendingPlanRefreshBusy = ref(false)
let refreshInterval = null

const tabs = [
  { name: 'ConsoleOverview', label: 'Overview', pathPrefix: '/console' },
  { name: 'ConsolePlans', label: 'Plans', pathPrefix: '/console/plans' },
  { name: 'ConsoleWorkspaces', label: 'Workspaces', pathPrefix: '/console/workspaces' },
  { name: 'ConsoleJobs', label: 'Runs', pathPrefix: '/console/jobs' },
  { name: 'ConsoleAnalytics', label: 'Analytics', pathPrefix: '/console/analytics' },
  { name: 'ToolsIndex', label: 'Tools', pathPrefix: '/console/tools' },
  { name: 'ConsoleSettings', label: 'Settings', pathPrefix: '/console/settings' },
]

function isTabActive(tab) {
  if (tab.name === 'ConsoleOverview') {
    return route.path === '/console' || route.path === '/console/'
  }
  return route.path.startsWith(tab.pathPrefix)
}

// #498: the Plans tab badge renders a bare number. Give the tab an explicit
// accessible name stating what the count is, so assistive tech announces
// "Plans, N pending approval" rather than "Plans N". The live dot is decorative
// (aria-hidden). The dot's old hover-only "auto-refresh every 30s" hint is
// intentionally NOT baked into the accessible name: operational cadence belongs
// in an aria-live region that actually announces count changes (tracked in
// #551), not in the element's name (where it would be present only at count>0,
// drift from the real interval, and lengthen every announcement). Other tabs
// fall back to their visible label.
function tabAriaLabel(tab) {
  if (tab.name === 'ConsolePlans' && pendingPlanCount.value > 0) {
    return `${tab.label}, ${pendingPlanCount.value} pending approval`
  }
  return null
}

async function refreshPendingPlanCount() {
  pendingPlanRefreshBusy.value = true
  try {
    const counts = await fetchPlanCounts(requestJson)
    pendingPlanCount.value = counts.pendingApprovalPlans
  } catch {
    // Keep the last successful count during transient refresh failures.
  } finally {
    pendingPlanRefreshBusy.value = false
  }
}

function scrollActiveTabIntoView() {
  nextTick(() => {
    const active = tabsEl.value?.querySelector('.console__tab--active')
    if (active) {
      active.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' })
    }
  })
}

watch(() => route.path, scrollActiveTabIntoView)

onMounted(() => {
  scrollActiveTabIntoView()
  void refreshPendingPlanCount()
  refreshInterval = setInterval(() => {
    void refreshPendingPlanCount()
  }, 30000)
})

onBeforeUnmount(() => {
  if (refreshInterval) {
    clearInterval(refreshInterval)
    refreshInterval = null
  }
})
</script>

<style src="@/styles/console.css"></style>
<style scoped>
.console__tab-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  margin-left: 8px;
  padding: 0 6px;
  border-radius: 999px;
  background: var(--shell-accent);
  color: var(--shell-background);
  font-size: 0.68rem;
  font-weight: 700;
  line-height: 1;
}

.console__tab-live-dot {
  display: inline-block;
  width: 7px;
  height: 7px;
  margin-left: 8px;
  border-radius: 50%;
  background: var(--status-active);
  animation: consoleTabLivePulse 1.8s ease-in-out infinite;
}

.console__tab-live-dot--busy {
  animation-duration: 0.9s;
}

@keyframes consoleTabLivePulse {
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }

  50% {
    opacity: 0.55;
    transform: scale(0.82);
  }
}

@media (prefers-reduced-motion: reduce) {
  .console__tab-live-dot,
  .console__tab-live-dot--busy {
    animation: none;
  }
}

.console__pending-plan-live {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
</style>
